import { useState } from 'react';
import { fileService } from 'services';
import { OnUploadProgressFunc } from 'services/apis/file';
import { UploadFile } from 'antd/es/upload/interface';
import { fileHelpers } from 'helpers';
import { fileConstants } from 'constants/index';
import heic2any from 'heic2any';
import { FILE_EXTENSION_MAPPING } from 'constants/mimeType';

const { downloadFileToDevice, getExtensionFile } = fileHelpers;
const { TYPE } = fileConstants;

const useFile = () => {
  const uploadFile = (
    file: File,
    onProgressCallback?: OnUploadProgressFunc
  ) => {
    return fileService.uploadFile(file, onProgressCallback);
  };

  const downloadFile = async (filePath: string) => {
    const data = await fileService.downloadFile(filePath);
    const url = URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filePath.replace('/', '')); // remove slash
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return { uploadFile, downloadFile };
};

const useHandlePreview = () => {
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewImg, setPreviewImg] = useState<UploadFile[]>();
  const [previewPosition, setPreviewPosition] = useState<number>(0);
  const [loadingPreviewImg, setLoadingPreviewImg] = useState<boolean>(false);

  const getBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handlePreview = async (file: any, fileList: any[]) => {
    try {
      setLoadingPreviewImg(true);
      setPreviewImg([]);
      setPreviewVisible(true);
      for (let i = 0; i < fileList.length; i++) {
        if (!fileList[i].preview) {
          if (!fileList[i].fileName) {
            const buffer = await getBase64(fileList[i].originFileObj);
            const fileType = getExtensionFile(fileList[i].name);

            if (fileType === TYPE.RAR) {
              downloadFileToDevice(buffer, fileList[i].name);
            } else {
              //@ts-ignore
              let blob = new Blob([new Uint8Array(buffer)], {
                type: fileType ? FILE_EXTENSION_MAPPING[fileType] : undefined,
              });
              if (fileType === TYPE.HEIC) {
                blob = (await heic2any({
                  blob: blob,
                  toType: TYPE.IMAGE,
                  quality: 1,
                })) as Blob;
              }
              fileList[i].preview = URL.createObjectURL(blob);
            }
          } else {
            const fileExtension = getExtensionFile(fileList[i].fileName);
            const data = await fileService.downloadFile(fileList[i].fileName);
            if (fileExtension === TYPE.RAR) {
              downloadFileToDevice(data, fileList[i].fileName);
            } else {
              let blob = new Blob([new Uint8Array(data)], {
                type: fileExtension
                  ? FILE_EXTENSION_MAPPING[fileExtension]
                  : undefined,
              });
              if (fileExtension === TYPE.HEIC) {
                blob = (await heic2any({
                  blob: blob,
                  toType: TYPE.IMAGE,
                  quality: 1,
                })) as Blob;
              }
              fileList[i].preview = URL.createObjectURL(blob);
            }
          }
        }
      }
    } finally {
      setLoadingPreviewImg(false);
    }

    setPreviewImg(fileList);
    setPreviewPosition(fileList.indexOf(file));
  };

  const viewFileFromNameMinioDesktop = async (fileName: string) => {
    try {
      setLoadingPreviewImg(true);
      var newTab = window.open();
      const buffer = await fileService.downloadFile(fileName);
      if (getExtensionFile(fileName) === TYPE.RAR) {
        downloadFileToDevice(buffer, fileName);
        newTab?.close();
      } else {
        let type;
        if (getExtensionFile(fileName) === TYPE.PDF_CL) {
          type = TYPE.PDF;
        } else if (
          getExtensionFile(fileName) === TYPE.MP4 ||
          getExtensionFile(fileName) === TYPE.MOV
        ) {
          type = TYPE.VIDEO;
        } else type = TYPE.IMAGE;
        let blob = new Blob([new Uint8Array(buffer)], { type });
        if (getExtensionFile(fileName) === TYPE.HEIC) {
          blob = (await heic2any({
            blob: blob,
            toType: TYPE.IMAGE,
            quality: 1,
          })) as Blob;
        }
        const fileURL = URL.createObjectURL(blob);
        if (newTab) {
          newTab?.location.replace(fileURL);
        }

        setTimeout(() => {
          if (newTab) newTab.document.title = fileName;
        }, 50);
      }
    } finally {
      setLoadingPreviewImg(false);
    }
  };
  return {
    previewVisible,
    previewImg,
    previewPosition,
    loadingPreviewImg,
    handlePreview,
    setPreviewVisible,
    viewFileFromNameMinioDesktop,
  };
};

const useDownloadImages = () => {
  const downloadImages = async (
    images: string[],
    isThumbnail: boolean = false
  ) => {
    const imagesPromise = images.map(async img => {
      return {
        file: await fileService.downloadFile(img, isThumbnail),
        fileName: img,
      };
    });
    let imagesData = await Promise.all(imagesPromise);
    return await Promise.all(
      imagesData.map(async img => {
        const fileType = getExtensionFile(img.fileName);
        let blob = new Blob([img['file']], {
          type: fileType ? FILE_EXTENSION_MAPPING[fileType] : undefined,
        });
        if (fileType === TYPE.HEIC) {
          blob = (await heic2any({
            blob: blob,
            toType: 'image/jpeg',
            quality: 1,
          })) as Blob;
        }
        return {
          uid: img.fileName + Date.now(),
          name: img.fileName,
          fileName: img.fileName,
          url: URL.createObjectURL(blob),
          size: blob.size,
        };
      })
    );
  };

  return {
    downloadImages,
  };
};

export default { useFile, useHandlePreview, useDownloadImages };
